import { useEffect, useRef, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import type { AvatarConfig } from '@contracts';
import { AssistantAvatar, SIZE_PX, isMascotAvatar } from '@/components/AssistantAvatar';

const DEFAULT_REMINDERS = [
  '¿Te ayudo con algo?',
  'Pregúntame por ventas, clientes o stock.',
  'Estoy aquí cuando me necesites.',
];

/**
 * Botón flotante del asistente: pinta la mascota (archivo de Odoo) grande, o la
 * estrella default, o —si el avatar está apagado— un botón discreto para invocar
 * el chat. Además avienta "recordatorios" (globos) cada N seg según AI_Admin.
 */
export function AssistantLauncher({ av, assistantCode, open, onToggle }: {
  av?: AvatarConfig | null;
  assistantCode: string;
  open: boolean;
  onToggle: () => void;
}) {
  const mascot = isMascotAvatar(av);
  const left = av?.position === 'bl';
  const sizePx = SIZE_PX[av?.size ?? 'lg'];

  const [bubble, setBubble] = useState<string | null>(null);
  const idx = useRef(0);
  const messages = av?.reminderMessages?.length ? av.reminderMessages : DEFAULT_REMINDERS;
  const interval = av?.reminderInterval ?? 0;

  // En móvil la mascota (config de Odoo = desktop) se capa para no tapar los widgets.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    setBubble(null);
    if (open || interval <= 0 || messages.length === 0) return;
    let hideT: ReturnType<typeof setTimeout>;
    const tick = () => {
      setBubble(messages[idx.current % messages.length]);
      idx.current += 1;
      hideT = setTimeout(() => setBubble(null), 5000);
    };
    const iv = setInterval(tick, interval * 1000);
    return () => {
      clearInterval(iv);
      clearTimeout(hideT);
    };
  }, [open, interval, messages]);

  // Botón flotante: mascota grande (si está activa) o la estrella naranja (chica)
  // que sirve tanto de avatar default como de invocador cuando la mascota se oculta.
  // Mascota: en móvil se capa a 110px (la config de Odoo es para escritorio).
  const effSize = isMobile && mascot ? Math.min(sizePx, 110) : sizePx;
  const circleCls = `grid h-14 w-14 place-items-center overflow-hidden rounded-full text-white shadow-xl bottom-6 ${left ? 'left-6' : 'right-6'}`;
  const btnClass = !open && mascot ? `bottom-2 ${left ? 'left-2' : 'right-2'}` : circleCls;
  const style = !open && mascot ? { height: effSize, width: effSize } : undefined;
  const bubbleBottom = (mascot ? effSize + 8 : 80) + 8;

  return (
    <>
      {bubble && !open && (
        <button
          onClick={onToggle}
          title="Abrir asistente"
          className={`fixed z-40 max-w-[230px] ${left ? 'left-4' : 'right-4'}`}
          style={{ bottom: bubbleBottom }}
        >
          <div className="rounded-2xl bg-white px-3 py-2 text-left text-sm font-medium text-slate-700 shadow-xl ring-1 ring-slate-200 transition hover:bg-slate-50 hover:ring-orange-300">
            {bubble}
          </div>
        </button>
      )}
      <button onClick={onToggle} title="Asistente" className={`fixed z-40 transition hover:scale-105 ${btnClass}`} style={style}>
        {open ? (
          <span className="grid h-full w-full place-items-center bg-gradient-to-br from-orange-500 to-orange-600">
            <X className="h-6 w-6" />
          </span>
        ) : mascot ? (
          <AssistantAvatar assistantCode={assistantCode} av={av} />
        ) : (
          <span className="grid h-full w-full place-items-center bg-gradient-to-br from-orange-500 to-orange-600">
            <Sparkles className="h-6 w-6" />
          </span>
        )}
      </button>
    </>
  );
}
